import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaMinus, FaArrowRight } from "react-icons/fa";

const faqs = [
  {
    question: "Who can apply to Zulfah Academy?",
    answer:
      "Anyone who is motivated, committed, has the zeal to learn, and is willing to commit to the programme. Whether you're a student, graduate, career switcher, entrepreneur, or professional looking to upgrade your skills, there's a pathway for you.",
  },
  {
    question: "Is the training physical, hybrid or online?",
    answer:
      "We offer flexible learning options including physical, hybrid and remote programmes. Learners can choose the format that best fits their location, schedule and learning preference.",
  },
  {
    question: "How much does a Zulfah Academy programme cost?",
    answer:
      "Our programmes have different tuition fees depending on the course and duration. Current programme fees generally range from ₦350,000 to ₦1,500,000. Visit the specific programme page for the latest tuition and payment information.",
  },
  {
    question: "Do I need prior programming experience?",
    answer:
      "Not necessarily. Several of our programmes are beginner-friendly and designed to take you from the fundamentals to practical, industry-ready skills. What matters most is your commitment to learning and willingness to practise.",
  },
  {
    question: "Is coding difficult to learn?",
    answer:
      "Coding can feel challenging at the beginning, but it is absolutely learnable. With structured training, consistent practice, mentorship and real-world projects, you can develop the confidence and skills needed to become a professional developer.",
  },
  {
    question: "Do I need to be good at Mathematics to learn programming?",
    answer:
      "No. While mathematics can be useful in certain areas of technology, you don't need to be a mathematics expert to become a good programmer. Logical thinking, problem-solving, consistency and practice are much more important for most software development roles.",
  },
  {
    question: "How long does it take to learn technology skills?",
    answer:
      "It depends on the programme and your level of commitment. Some programmes can be completed in a few months, while more comprehensive programmes may take six months or longer. Our focus is not simply on finishing a course but on helping you develop practical, employable skills.",
  },
  {
    question: "Will I work on real-world projects?",
    answer:
      "Yes. Our training approach is strongly focused on practical learning. Depending on your programme, you will work on projects designed to help you understand how technology is applied in real organisations and products.",
  },
  {
    question: "Can organisations partner with Zulfah Academy?",
    answer:
      "Absolutely. We work with organisations, schools, companies and other institutions on technology training, talent development, capacity building and strategic partnerships.",
  },
  {
    question: "How can I get more information?",
    answer:
      "You can contact our team through info@zulfahgroup.com or +234 811 537 1014. Our team will be happy to answer your questions and help you identify the right programme.",
  },
];

const Faq = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleFaq = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="relative overflow-hidden bg-[#071014] py-28 text-white"
    >
      {/* =====================================================
          BACKGROUND ATMOSPHERE
      ====================================================== */}

      <div className="absolute inset-0 pointer-events-none">

        {/* Cyan glow */}
        <div
          className="
            absolute
            -top-40
            -left-40
            h-[500px]
            w-[500px]
            rounded-full
            bg-[#59b5cd]/10
            blur-[130px]
          "
        />

        {/* Yellow glow */}
        <div
          className="
            absolute
            top-1/3
            -right-40
            h-[500px]
            w-[500px]
            rounded-full
            bg-[#ecb32c]/10
            blur-[130px]
          "
        />

        {/* Grid */}
        <div
          className="
            absolute
            inset-0
            opacity-[0.035]
            bg-[linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)]
            bg-[size:60px_60px]
          "
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-16"
        >
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-6">

            <span className="h-[1px] w-10 bg-[#ecb32c]" />

            <span
              className="
                text-xs
                md:text-sm
                uppercase
                tracking-[0.25em]
                font-bold
                text-[#ecb32c]
              "
            >
              Frequently Asked Questions
            </span>

          </div>

          <h2
            className="
              text-4xl
              sm:text-5xl
              md:text-6xl
              font-black
              leading-[1.05]
              tracking-tight
            "
          >
            Everything you need
            <br />

            <span
              className="
                bg-gradient-to-r
                from-[#59b5cd]
                via-[#59b5cd]
                to-[#ecb32c]
                bg-clip-text
                text-transparent
              "
            >
              to know before you start.
            </span>
          </h2>

          <p className="mt-7 text-base md:text-lg text-white/55 max-w-2xl leading-relaxed">
            Starting a career in technology is a big decision. We've answered
            some of the questions learners ask us most often.
          </p>
        </motion.div>

        {/* =====================================================
            FAQ LAYOUT
        ====================================================== */}

        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20 items-start">

          {/* LEFT SIDE */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:sticky lg:top-28"
          >

            <div
              className="
                relative
                overflow-hidden
                rounded-[28px]
                border
                border-white/10
                bg-white/[0.04]
                p-8
                md:p-10
                backdrop-blur-xl
              "
            >

              {/* Decorative circle */}
              <div
                className="
                  absolute
                  -right-20
                  -top-20
                  h-48
                  w-48
                  rounded-full
                  border
                  border-[#59b5cd]/20
                "
              />

              <div
                className="
                  absolute
                  -right-10
                  -top-10
                  h-28
                  w-28
                  rounded-full
                  bg-[#59b5cd]/10
                  blur-2xl
                "
              />

              <span className="relative text-7xl md:text-8xl font-black text-white/10">
                ?
              </span>

              <h3 className="relative mt-3 text-2xl md:text-3xl font-bold">
                Still have questions?
              </h3>

              <p className="relative mt-4 text-white/50 leading-relaxed">
                Our team is always available to help you choose the right
                learning path and understand what your journey at Zulfah
                Academy looks like.
              </p>

              <a
                href="htpps://wa.me/2348115371014?text=Hello Zulfah team, Please I want to make enquiry"
                className="
                  relative
                  inline-flex
                  items-center
                  gap-3
                  mt-8
                  rounded-full
                  bg-[#ecb32c]
                  px-6
                  py-3.5
                  text-sm
                  font-bold
                  text-[#071014]
                  transition
                  hover:scale-105
                  hover:shadow-[0_0_30px_rgba(236,179,44,0.25)]
                "
              >
                Talk to our team
                <FaArrowRight className="text-xs" />
              </a>
            </div>

            {/* Mini stat */}
            <div className="mt-5 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.025] p-5">

              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#59b5cd]/10 text-[#59b5cd] font-bold">
                Z
              </div>

              <div>
                <p className="font-semibold">
                  Your next chapter starts here.
                </p>

                <p className="text-sm text-white/40 mt-1">
                  Learn. Build. Launch.
                </p>
              </div>

            </div>
          </motion.div>

          {/* RIGHT FAQ */}
          <div className="space-y-3">

            {faqs.map((faq, index) => {
              const isOpen = activeIndex === index;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.45,
                    delay: index * 0.04,
                  }}
                  className={`
                    group
                    overflow-hidden
                    rounded-2xl
                    border
                    transition-all
                    duration-300
                    ${
                      isOpen
                        ? "border-[#59b5cd]/30 bg-white/[0.07]"
                        : "border-white/10 bg-white/[0.025] hover:border-white/20 hover:bg-white/[0.045]"
                    }
                  `}
                >

                  {/* QUESTION */}
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center gap-5 text-left px-5 md:px-7 py-6"
                  >

                    {/* Number */}
                    <span
                      className={`
                        flex-shrink-0
                        text-xs
                        font-bold
                        tracking-wider
                        transition-colors
                        ${
                          isOpen
                            ? "text-[#ecb32c]"
                            : "text-white/25"
                        }
                      `}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    {/* Question */}
                    <span
                      className={`
                        flex-1
                        text-base
                        md:text-lg
                        font-semibold
                        transition-colors
                        ${
                          isOpen
                            ? "text-white"
                            : "text-white/75 group-hover:text-white"
                        }
                      `}
                    >
                      {faq.question}
                    </span>

                    {/* Icon */}
                    <span
                      className={`
                        flex
                        h-9
                        w-9
                        flex-shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border
                        transition-all
                        ${
                          isOpen
                            ? "border-[#ecb32c] bg-[#ecb32c] text-[#071014]"
                            : "border-white/10 text-white/40"
                        }
                      `}
                    >
                      {isOpen ? (
                        <FaMinus className="text-xs" />
                      ) : (
                        <FaPlus className="text-xs" />
                      )}
                    </span>

                  </button>

                  {/* ANSWER */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{
                          height: 0,
                          opacity: 0,
                        }}
                        animate={{
                          height: "auto",
                          opacity: 1,
                        }}
                        exit={{
                          height: 0,
                          opacity: 0,
                        }}
                        transition={{
                          duration: 0.3,
                          ease: "easeInOut",
                        }}
                      >
                        <div className="px-5 md:px-7 pb-7 pl-[4.1rem] md:pl-[4.8rem]">

                          <div className="h-px bg-white/10 mb-5" />

                          <p className="text-sm md:text-base text-white/50 leading-relaxed max-w-2xl">
                            {faq.answer}
                          </p>

                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                </motion.div>
              );
            })}

          </div>
        </div>

        {/* =====================================================
            BOTTOM CTA
        ====================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="
            relative
            overflow-hidden
            mt-24
            rounded-[30px]
            border
            border-[#59b5cd]/20
            bg-gradient-to-r
            from-[#59b5cd]/10
            via-white/[0.03]
            to-[#ecb32c]/10
            p-8
            md:p-12
          "
        >

          {/* Glow */}
          <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#ecb32c]/10 blur-[90px]" />

          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">

            <div>
              <p className="text-[#ecb32c] text-sm font-bold uppercase tracking-[0.2em]">
                Ready to begin?
              </p>

              <h3 className="mt-3 text-2xl md:text-4xl font-black">
                Your future in tech
                <span className="text-[#59b5cd]"> starts now.</span>
              </h3>
            </div>

            <a
              href="/get-started"
              className="
                inline-flex
                items-center
                justify-center
                gap-3
                rounded-full
                bg-white
                px-7
                py-4
                text-sm
                font-bold
                text-[#071014]
                transition
                hover:bg-[#ecb32c]
                hover:scale-105
              "
            >
              Start Your Journey
              <FaArrowRight className="text-xs" />
            </a>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Faq;